import { useEffect, useState } from "react";
import { FileText, Loader2, Download, Ban, Settings, RefreshCw, Play, ArrowLeft } from "lucide-react";
import { api, API, inr } from "../../lib/api";
import { toast } from "sonner";
import AdminShopPicker from "../../components/AdminShopPicker";
import { openOrDownloadPdf, openPdfUrl, filenameFromContentDisposition } from "../../lib/pdfDownload";
import PdfViewerModal from "../../lib/PdfViewerModal";

/**
 * iter108 — Admin Commission Invoice management.
 *
 * Two tabs:
 *   • "Invoices"  — list, filter by shop/year, download PDF, void
 *   • "Settings"  — enable + billing cycle + GST + platform GSTIN etc.
 *
 * The full settings surface lives here (not in Super Admin) because
 * the user asked for tenant-level configurability — every tenant
 * runs their own tax profile. Super Admin can still edit the same
 * fields via the Tenant edit modal.
 */
export default function AdminCommissionInvoices() {
    // iter108 — honor ?tab=SETTINGS deep-links from support / notifications.
    const initialTab = typeof window !== "undefined"
        && new URLSearchParams(window.location.search).get("tab") === "SETTINGS"
        ? "SETTINGS" : "INVOICES";
    const [tab, setTab] = useState(initialTab);

    return (
        <div data-testid="admin-commission-invoices">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                        <FileText size={22} /> Seller Commission Invoices
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        GST-compliant invoices for the commission you charge each seller. Configure billing cycle and tax profile below — off by default.
                    </p>
                </div>
                <div className="flex gap-1">
                    <TabBtn active={tab === "INVOICES"} onClick={() => setTab("INVOICES")} testid="ci-tab-invoices">Invoices</TabBtn>
                    <TabBtn active={tab === "SETTINGS"} onClick={() => setTab("SETTINGS")} testid="ci-tab-settings"><Settings size={13} className="inline -mt-0.5 mr-1" />Settings</TabBtn>
                </div>
            </div>

            {tab === "INVOICES" ? <InvoicesTab /> : <SettingsTab />}
        </div>
    );
}


// ------------------------------------------------------------
// Invoices tab
// ------------------------------------------------------------
function InvoicesTab() {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [shopFilter, setShopFilter] = useState("");
    const [year, setYear] = useState(new Date().getFullYear());
    const [cycle, setCycle] = useState("MONTHLY");

    const load = async (opts = {}) => {
        const { silent = false, showToast = false } = opts;
        if (!silent) setRefreshing(true);
        try {
            const params = new URLSearchParams({ year });
            if (shopFilter) params.set("restaurant_id", shopFilter);
            const { data } = await api.get(`/admin/commission-invoices?${params}`);
            setRows(data.rows || []);
            if (showToast) {
                const n = (data.rows || []).length;
                toast.success(`Loaded ${n} invoice${n === 1 ? "" : "s"} for ${year}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
            setRows([]);
        } finally {
            if (!silent) setRefreshing(false);
        }
    };
    // Silent initial-load + on-filter-change (no toast). The visible
    // Refresh button uses showToast=true so the click has feedback.
    useEffect(() => { load({ silent: true }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [shopFilter, year]);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/commission-invoices/generate`, {
                restaurant_id: shopFilter || null,
                billing_cycle: cycle,
            });
            const rep = data.report || {};
            const period = rep.period_start && rep.period_end
                ? ` (${rep.period_start} → ${rep.period_end})`
                : "";
            if ((rep.newly_created ?? 0) > 0) {
                toast.success(
                    `Generated ${rep.newly_created} new invoice${rep.newly_created === 1 ? "" : "s"}` +
                    ((rep.refreshed ?? 0) > 0 ? ` and refreshed ${rep.refreshed}` : "") +
                    period,
                );
            } else if ((rep.refreshed ?? 0) > 0) {
                toast.success(
                    `Refreshed ${rep.refreshed} invoice${rep.refreshed === 1 ? "" : "s"} with the latest settlement data${period}.`,
                );
            } else if ((rep.shops_considered ?? 0) === 0) {
                toast.warning(`No active shops found${period}. Add / activate shops before generating invoices.`);
            } else if ((rep.skipped_zero_commission ?? 0) === rep.shops_considered) {
                toast.warning(
                    `No commission to bill${period}. All ${rep.shops_considered} shop(s) had ₹0 commission — ` +
                    `check that shop settlements have been generated for that window (Admin → Shop Settlements → Generate).`,
                );
            } else if ((rep.already_existed ?? 0) > 0 && (rep.newly_created ?? 0) === 0) {
                toast.info(
                    `Invoices for ${rep.period_key} already exist. Nothing new to generate${period}.`,
                );
            } else {
                toast.info(`Nothing to generate${period}. Skipped ${rep.skipped_zero_commission || 0} zero-commission shop(s).`);
            }
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally { setBusy(false); }
    };

    // iter134m — Native path renders the PDF INSIDE the app via
    // ``PdfViewerModal`` (pdf.js). This works regardless of whether
    // ``@capacitor/browser`` was ``cap sync``'d and doesn't depend on
    // the WebView having a native PDF renderer. Web keeps the fast
    // blob→anchor download flow.
    const [pdfModal, setPdfModal] = useState(null);   // { endpoint, filename }

    const download = async (inv) => {
        const filename = `commission-${inv.invoice_number.replace(/\//g, "-")}.pdf`;
        const cap = typeof window !== "undefined" ? window.Capacitor : null;
        if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) {
            setPdfModal({ endpoint: `/admin/commission-invoices/${inv.id}/pdf`, filename });
            return;
        }
        try {
            const { data, headers } = await api.get(
                `/admin/commission-invoices/${inv.id}/pdf`, { responseType: "blob" },
            );
            const ctype = (headers["content-type"] || "").toLowerCase();
            if (!ctype.includes("application/pdf")) {
                const text = await data.text();
                toast.error(text?.slice(0, 200) || "Server did not return a PDF");
                return;
            }
            const blob = new Blob([data], { type: "application/pdf" });
            const fn = filenameFromContentDisposition(headers["content-disposition"], filename);
            await openOrDownloadPdf(blob, fn);
        } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    };

    const voidInvoice = async (inv) => {
        const reason = window.prompt(`Void invoice ${inv.invoice_number}? Please enter a reason:`);
        if (!reason) return;
        try {
            await api.post(`/admin/commission-invoices/${inv.id}/void`, { reason });
            toast.success("Invoice voided");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Void failed"); }
    };

    return (
        <div className="mt-5">
            <div className="flex items-center flex-wrap gap-2">
                <select data-testid="ci-year" className="rrinp" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {[0, 1, 2].map((offset) => {
                        const y = new Date().getFullYear() - offset;
                        return <option key={y} value={y}>{y}</option>;
                    })}
                </select>
                <AdminShopPicker
                    testIdPrefix="ci-shop"
                    value={shopFilter}
                    onChange={setShopFilter}
                    allShopsOption
                />
                <select data-testid="ci-cycle" className="rrinp" value={cycle} onChange={(e) => setCycle(e.target.value)}>
                    <option value="MONTHLY">Monthly</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="ON_DEMAND">On-demand (last 30 days)</option>
                </select>
                <button
                    data-testid="ci-generate"
                    onClick={generate}
                    disabled={busy}
                    className="bg-primary text-primary-foreground rounded-xl px-3 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50"
                >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                    Generate for previous {cycle === "WEEKLY" ? "week" : cycle === "ON_DEMAND" ? "30 days" : "month"}
                </button>
                <button
                    data-testid="ci-refresh"
                    onClick={() => load({ showToast: true })}
                    disabled={refreshing}
                    className="rounded-xl border border-border px-3 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-50"
                >
                    {refreshing
                        ? <Loader2 size={13} className="animate-spin" />
                        : <RefreshCw size={13} />}
                    Refresh
                </button>
            </div>

            <div className="mt-4 rounded-2xl border border-border overflow-hidden">
                {/* Desktop / tablet — full table. Hidden on mobile. */}
                <table className="w-full text-sm hidden md:table">
                    <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Invoice #</th>
                            <th className="px-3 py-2 text-left">Shop</th>
                            <th className="px-3 py-2 text-left">Period</th>
                            <th className="px-3 py-2 text-right">Orders</th>
                            <th className="px-3 py-2 text-right">Commission</th>
                            <th className="px-3 py-2 text-right">GST</th>
                            <th className="px-3 py-2 text-right">Total</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows === null ? (
                            <tr><td colSpan={8} className="text-center py-10"><Loader2 size={18} className="inline animate-spin" /></td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">
                                No invoices yet — click <b>Generate</b> above to create them.
                            </td></tr>
                        ) : rows.map((r) => {
                            const gst = (r.cgst_amount || 0) + (r.sgst_amount || 0) + (r.igst_amount || 0);
                            const voided = r.status === "VOIDED";
                            return (
                                <tr key={r.id} data-testid={`ci-row-${r.id}`} className={`border-t border-border ${voided ? "opacity-60 line-through" : ""}`}>
                                    <td className="px-3 py-2 font-semibold">{r.invoice_number}</td>
                                    <td className="px-3 py-2">{r.restaurant_name || "—"}</td>
                                    <td className="px-3 py-2">{r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{r.orders_count}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{inr(r.commission_amount)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {gst > 0
                                            ? <span title={r.gst_type === "CGST_SGST" ? `CGST ${inr(r.cgst_amount)} + SGST ${inr(r.sgst_amount)}` : `IGST ${inr(r.igst_amount)}`}>
                                                {inr(gst)}
                                            </span>
                                            : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right font-bold tabular-nums">{inr(r.total_amount)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            data-testid={`ci-pdf-${r.id}`}
                                            onClick={() => download(r)}
                                            title="Download PDF"
                                            className="p-1.5 rounded hover:bg-muted"
                                        ><Download size={14} /></button>
                                        {!voided && (
                                            <button
                                                data-testid={`ci-void-${r.id}`}
                                                onClick={() => voidInvoice(r)}
                                                title="Void invoice"
                                                className="p-1.5 rounded hover:bg-destructive/10 text-destructive ml-1"
                                            ><Ban size={14} /></button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {/* Mobile — stacked cards. Shown below md breakpoint. */}
                <div className="md:hidden divide-y divide-border">
                    {rows === null ? (
                        <div className="text-center py-10"><Loader2 size={18} className="inline animate-spin" /></div>
                    ) : rows.length === 0 ? (
                        <div className="text-center py-10 text-sm text-muted-foreground px-4">
                            No invoices yet — tap <b>Generate</b> above to create them.
                        </div>
                    ) : rows.map((r) => {
                        const gst = (r.cgst_amount || 0) + (r.sgst_amount || 0) + (r.igst_amount || 0);
                        const voided = r.status === "VOIDED";
                        return (
                            <div
                                key={r.id}
                                data-testid={`ci-row-${r.id}`}
                                className={`p-3 ${voided ? "opacity-60 line-through" : ""}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Invoice</p>
                                        <p className="text-sm font-semibold break-all leading-tight">{r.invoice_number}</p>
                                        <p className="mt-1 text-sm text-foreground/80 truncate">{r.restaurant_name || "—"}</p>
                                        <p className="text-[11px] text-muted-foreground mt-0.5">
                                            {r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)} · {r.orders_count} order{r.orders_count === 1 ? "" : "s"}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
                                        <p className="text-base font-bold tabular-nums leading-tight">{inr(r.total_amount)}</p>
                                        {gst > 0 && (
                                            <p className="text-[11px] text-muted-foreground tabular-nums">
                                                GST {inr(gst)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-2 flex items-center justify-between gap-2">
                                    <span className="text-[11px] text-muted-foreground">
                                        Commission <span className="tabular-nums font-semibold text-foreground">{inr(r.commission_amount)}</span>
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <button
                                            data-testid={`ci-pdf-${r.id}`}
                                            onClick={() => download(r)}
                                            className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold active:scale-95 transition-transform"
                                        >
                                            <Download size={13} /> PDF
                                        </button>
                                        {!voided && (
                                            <button
                                                data-testid={`ci-void-${r.id}`}
                                                onClick={() => voidInvoice(r)}
                                                className="inline-flex items-center gap-1 rounded-lg border border-destructive/40 text-destructive px-2.5 py-1.5 text-xs font-semibold active:scale-95 transition-transform"
                                                title="Void invoice"
                                            >
                                                <Ban size={13} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {pdfModal && (
                <PdfViewerModal
                    endpoint={pdfModal.endpoint}
                    filename={pdfModal.filename}
                    onClose={() => setPdfModal(null)}
                />
            )}
        </div>
    );
}


// ------------------------------------------------------------
// Settings tab
// ------------------------------------------------------------
function SettingsTab() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/commission-invoice-config").then(({ data }) => setCfg(data)).catch(() => setCfg({}));
    }, []);

    if (cfg === null) return <div className="mt-8 text-center"><Loader2 className="inline animate-spin" /></div>;

    const set = (patch) => setCfg({ ...cfg, ...patch });

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/admin/commission-invoice-config", cfg);
            toast.success("Settings saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="mt-5 max-w-3xl">
            <div className="grid gap-4">
                <FieldRow label="Enable commission invoicing" hint="When off, no invoices are generated. Existing invoices remain accessible.">
                    <ToggleSwitch data-testid="cfg-enabled" value={cfg.enabled} onChange={(v) => set({ enabled: v })} />
                </FieldRow>
                <FieldRow label="Billing cycle" hint="Monthly generates on the 1st. Weekly runs Mondays. Others are manual.">
                    <select data-testid="cfg-cycle" className="inp" value={cfg.billing_cycle || "MONTHLY"} onChange={(e) => set({ billing_cycle: e.target.value })}>
                        <option value="MONTHLY">Monthly (auto on 1st)</option>
                        <option value="WEEKLY">Weekly (auto on Mondays)</option>
                        <option value="PER_SETTLEMENT">Per settlement (manual)</option>
                        <option value="ON_DEMAND">On-demand only</option>
                    </select>
                </FieldRow>
                <FieldRow label="Invoice # prefix" hint="e.g. INV/ produces INV/2026/00001">
                    <input data-testid="cfg-prefix" className="inp" value={cfg.invoice_number_prefix || "INV/"} onChange={(e) => set({ invoice_number_prefix: e.target.value })} />
                </FieldRow>

                <div className="border-t border-border pt-4 mt-2">
                    <h3 className="font-bold text-sm mb-2">Tax Profile</h3>
                    <FieldRow label="Charge GST on commission" hint="OFF for tenants not registered under GST. When ON, provide GSTIN below.">
                        <ToggleSwitch data-testid="cfg-gst" value={cfg.gst_enabled} onChange={(v) => set({ gst_enabled: v })} />
                    </FieldRow>
                    {cfg.gst_enabled && (
                        <>
                            <FieldRow label="GST rate %" hint="18% is standard for platform commission services.">
                                <input data-testid="cfg-rate" type="number" step="0.01" className="inp" value={cfg.gst_rate_pct ?? 18} onChange={(e) => set({ gst_rate_pct: Number(e.target.value) })} />
                            </FieldRow>
                            <FieldRow label="SAC code" hint="Default 999693 (Support services). Change if you invoice under a different service code.">
                                <input data-testid="cfg-sac" className="inp" value={cfg.sac_code || ""} onChange={(e) => set({ sac_code: e.target.value })} />
                            </FieldRow>
                        </>
                    )}
                </div>

                <div className="border-t border-border pt-4 mt-2">
                    <h3 className="font-bold text-sm mb-2">Platform Identity (issuer)</h3>
                    <p className="text-xs text-muted-foreground mb-3">Printed on every invoice. Update your legal name, GSTIN and address before enabling GST.</p>
                    <FieldRow label="Legal name *">
                        <input data-testid="cfg-legal-name" className="inp" value={cfg.platform_legal_name || ""} onChange={(e) => set({ platform_legal_name: e.target.value })} placeholder="Bizil Retail Pvt Ltd" />
                    </FieldRow>
                    <FieldRow label="GSTIN">
                        <input data-testid="cfg-gstin" className="inp" value={cfg.platform_gstin || ""} onChange={(e) => set({ platform_gstin: e.target.value.toUpperCase() })} placeholder="36AABCU9603R1ZM" />
                    </FieldRow>
                    <FieldRow label="PAN">
                        <input data-testid="cfg-pan" className="inp" value={cfg.platform_pan || ""} onChange={(e) => set({ platform_pan: e.target.value.toUpperCase() })} placeholder="AABCU9603R" />
                    </FieldRow>
                    <FieldRow label="Registered address">
                        <textarea data-testid="cfg-address" rows={2} className="inp" value={cfg.platform_address || ""} onChange={(e) => set({ platform_address: e.target.value })} />
                    </FieldRow>
                    <FieldRow label="Registered state" hint="Determines CGST+SGST vs IGST split against each shop's state.">
                        <input data-testid="cfg-state" className="inp" value={cfg.platform_state || ""} onChange={(e) => set({ platform_state: e.target.value })} placeholder="Telangana" />
                    </FieldRow>
                </div>

                <div className="pt-4">
                    <button data-testid="cfg-save" onClick={save} disabled={saving} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm disabled:opacity-50">
                        {saving ? <Loader2 size={14} className="inline animate-spin" /> : "Save settings"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function TabBtn({ active, onClick, testid, children }) {
    return (
        <button data-testid={testid} onClick={onClick}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap ${active ? "bg-foreground text-background border-foreground" : "border-border"}`}
        >{children}</button>
    );
}


function FieldRow({ label, hint, children }) {
    return (
        <div className="grid sm:grid-cols-3 gap-2 items-start">
            <div>
                <label className="text-xs font-bold uppercase tracking-wider">{label}</label>
                {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
            </div>
            <div className="sm:col-span-2">{children}</div>
        </div>
    );
}


function ToggleSwitch({ value, onChange, ...rest }) {
    return (
        <button type="button" onClick={() => onChange(!value)} {...rest}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${value ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`}
        >
            {value ? "Enabled" : "Disabled"}
        </button>
    );
}
